import React, { useEffect, useRef, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

/* Fix the Quill editor content height to ~3 lines inside this modal */
const quillStyle = `
  .preset-quill .ql-editor {
    min-height: 72px;
    max-height: 72px;
    overflow-y: auto;
    font-size: 0.9rem;
    line-height: 1.5;
  }
`;


const MAX_NAME_LENGTH = 50;

const TicketPresetModal = ({ onClose, onSave, initialData = null }) => {
  const modalRef = useRef(null);
  const [presetName, setPresetName] = useState(initialData?.preset_name || "");
  const [presetContent, setPresetContent] = useState(initialData?.preset || "");
  const [nameError, setNameError] = useState("");
  const [contentError, setContentError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setPresetName(initialData?.preset_name || "");
    setPresetContent(initialData?.preset || "");
  }, [initialData]);

  // Close on outside click or Escape
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) onClose();
    };
    const handleEscape = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [onClose]);

  const handleSubmit = async () => {
    let valid = true;
    setNameError("");
    setContentError("");

    if (!presetName.trim()) {
      setNameError("Preset name is required.");
      valid = false;
    } else if (presetName.trim().length > MAX_NAME_LENGTH) {
      setNameError(`Preset name must be ${MAX_NAME_LENGTH} characters or fewer.`);
      valid = false;
    }

    const stripped = presetContent.replace(/<[^>]*>/g, "").trim();
    if (!stripped) {
      setContentError("Preset content cannot be empty.");
      valid = false;
    }

    if (!valid) return;

    setIsSubmitting(true);
    try {
      await onSave({
        ...(initialData?.id ? { id: initialData.id } : {}),
        preset_name: presetName.trim(),
        preset: presetContent,
      });
      onClose();
    } catch (err) {
      console.error("Error saving preset:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-3 sm:p-4">
      <style>{quillStyle}</style>
      <div
        ref={modalRef}
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl flex flex-col"
        style={{ maxHeight: "90vh" }}
      >
        {/* Header */}
        <div className="flex-none flex justify-between items-start gap-3 p-4 sm:p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{initialData ? "Edit Ticket Preset" : "Add Ticket Preset"}</h2>
            <p className="text-sm text-gray-500 mt-1">{initialData ? "Update the selected reply template" : "Create a reusable reply template"}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-lg hover:bg-gray-100"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 p-4 sm:p-6 overflow-y-auto space-y-6">
          {/* Name field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Set the name for your preset{" "}
              <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={presetName}
              maxLength={MAX_NAME_LENGTH}
              onChange={(e) => {
                setPresetName(e.target.value);
                if (nameError) setNameError("");
              }}
              placeholder="e.g. Thank you for contacting us"
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#90c740] focus:border-[#90c740] transition-colors duration-200 placeholder-gray-400 ${
                nameError ? "border-red-400 bg-red-50" : "border-gray-300"
              }`}
            />
            <div className="flex justify-between mt-1">
              {nameError ? (
                <p className="text-xs text-red-600">{nameError}</p>
              ) : (
                <span />
              )}
              <p
                className={`text-xs ml-auto ${
                  presetName.length >= MAX_NAME_LENGTH
                    ? "text-red-500 font-semibold"
                    : "text-gray-400"
                }`}
              >
                {presetName.length}/{MAX_NAME_LENGTH}
              </p>
            </div>
          </div>

          {/* Rich text editor */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Preset Content <span className="text-red-500">*</span>
            </label>
            <div className={`preset-quill rounded-lg overflow-hidden border ${contentError ? "border-red-400" : "border-gray-300"}`}>
              <ReactQuill
                theme="snow"
                value={presetContent}
                onChange={(val) => {
                  setPresetContent(val);
                  if (contentError) setContentError("");
                }}
                placeholder="Write your preset reply here..."
              />
            </div>
            {contentError && (
              <p className="text-xs text-red-600 mt-1">{contentError}</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex-none flex flex-col-reverse gap-3 p-4 sm:flex-row sm:justify-end sm:gap-4 sm:p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="w-full px-6 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed sm:w-auto"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="w-full justify-center px-6 py-3 bg-[#90c740] text-white rounded-lg hover:bg-[#7ebd2e] transition-colors font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 sm:w-auto"
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Saving...
              </>
            ) : (
              initialData ? "Update Preset" : "Save Preset"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TicketPresetModal;
